import './override.scss';
import FooterNav from '@xo-union/tk-component-footer-nav/remote';
import Layout from '@xo-union/tk-component-layout';
import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import InitialLoad from './components/InitialLoad';
import MessagingHub from './components/MessagingHub';
import AddressCollectionEmail from './components/MessagingHub/Sequences/AddressCollectionEmail';
import AddressCollectionSMS from './components/MessagingHub/Sequences/AddressCollectionSms';
import CovidEmail from './components/MessagingHub/Sequences/CovidEmail';
import CovidSMS from './components/MessagingHub/Sequences/CovidSms';
import CustomEmail from './components/MessagingHub/Sequences/CustomEmail';
import LivestreamEmail from './components/MessagingHub/Sequences/LivestreamEmail';
import RsvpDeadlineReminderEmail from './components/MessagingHub/Sequences/RsvpDeadlineReminderEmail';
import RsvpDeadlineReminderSms from './components/MessagingHub/Sequences/RsvpDeadlineReminderSms';
import SaveTheDateEmail from './components/MessagingHub/Sequences/SaveTheDateEmail';
import SendUpdatesEmail from './components/MessagingHub/Sequences/SendUpdatesEmail';
import SendUpdatesSMS from './components/MessagingHub/Sequences/SendUpdatesSms';
import ShareDayOfDetailsEmail from './components/MessagingHub/Sequences/ShareDayOfDetailsEmail';
import ShareDayOfDetailsSMS from './components/MessagingHub/Sequences/ShareDayOfDetailsSms';
import ShareWebsiteEmail from './components/MessagingHub/Sequences/ShareWebsiteEmail';
import ShareWebsiteSMS from './components/MessagingHub/Sequences/ShareWebsiteSms';
import ShareLocalHotelEmail from './components/MessagingHub/Sequences/ShareLocalHotelEmail';
import ShareLocalHotelSMS from './components/MessagingHub/Sequences/ShareLocalHotelSMS';
import Preview from './components/PreviewPage/EditInPreview';
import RSVPSettingsPage from './components/RsvpSettingsPage';
import { SafeCropContext, setupSafeCrop } from './components/SafeCrop';
import { usePagesQuery, useUnleashFeatureToggleQuery } from './generated/graphql';
import HeaderNav from './HeaderNav';
import {
  addressCollectionEmailRoute,
  addressCollectionSMSRoute,
  covidEmailRoute,
  covidSMSRoute,
  customEmailRoute,
  livestreamEmailRoute,
  messagingHubRoute,
  previewRoute,
  rsvpDeadlineReminderEmailRoute,
  rsvpDeadlineReminderSmsRoute,
  rsvpSettingsRoute,
  rsvpWorkflowRoute,
  shareWebsiteEmailRoute,
  shareWebsiteSMSRoute,
  saveTheDateEmailRoute,
  sendUpdatesEmailRoute,
  sendUpdatesSMSRoute,
  shareDayOfDetailsEmailRoute,
  shareDayOfDetailsSMSRoute,
  onboardingDesignPLPRoute,
  shareLocalHotelEmailRoute,
  shareLocalHotelSMSRoute,
} from './helpers/routes';
import Loading from './Loading';
import Main from './Main';
import { TrackingProvider } from './TrackingContext';
import { VariantProvider } from './VariantContext';
import './App.scss';
import GuestListManagerApp from './glm/app';
import WwsOnboarding from './wws-onboarding/App';
import { FeatureToggleProvider } from './context/FeatureTogglesContext';
import { WebsiteLayoutProvider } from './context/WebsiteLayoutContext';
import { AiTextGenerationProvider } from './context/AiTextGenerationContext';
import { AiTextExperimentProvider } from './context/AiTextExperimentContext';

const RsvpWorkflow = React.lazy(() => import('./components/RsvpWorkflow'));
const product = process.env.REACT_APP_ANALYTICS_PRODUCT;
const analyticsProps = { product };
const App: React.FC = () => {
  const { data } = usePagesQuery();
  const pages = data?.pages || [];
  const getSafeCrop = setupSafeCrop(pages);
  const { data: unleashFeatureTogglesData } = useUnleashFeatureToggleQuery();
  const featureToggles = unleashFeatureTogglesData?.unleashFeatureToggles || [];

  return (
    <FeatureToggleProvider featureToggles={featureToggles}>
      <VariantProvider>
        <TrackingProvider>
          <WebsiteLayoutProvider>
            <AiTextExperimentProvider>
              <AiTextGenerationProvider>
                <Router>
                  <Switch>
                    <Route path={['/gs/wedding-websites', onboardingDesignPLPRoute, '/gs/designs']}>
                      <WwsOnboarding />
                    </Route>
                    <Route>
                      <Layout
                        header={<HeaderNav product={product} />}
                        footer={<FooterNav analyticsProps={analyticsProps} />}
                      >
                        <Router>
                          <React.Suspense fallback={<Loading />}>
                            <SafeCropContext.Provider value={{ getSafeCrop }}>
                              <Route path={['/', '/gs/dashboard']}>
                                <InitialLoad />
                              </Route>
                              <Switch>
                                <Route path={rsvpWorkflowRoute}>
                                  <RsvpWorkflow />
                                </Route>
                                <Route path={previewRoute}>
                                  <Preview />
                                </Route>
                                <Route path={saveTheDateEmailRoute}>
                                  <SaveTheDateEmail />
                                </Route>
                                <Route path={shareWebsiteEmailRoute}>
                                  <ShareWebsiteEmail />
                                </Route>
                                <Route path={shareWebsiteSMSRoute}>
                                  <ShareWebsiteSMS />
                                </Route>
                                <Route path={covidEmailRoute}>
                                  <CovidEmail />
                                </Route>
                                <Route path={covidSMSRoute}>
                                  <CovidSMS />
                                </Route>
                                <Route path={customEmailRoute}>
                                  <CustomEmail />
                                </Route>
                                <Route path={rsvpSettingsRoute}>
                                  <RSVPSettingsPage />
                                </Route>
                                <Route path={addressCollectionEmailRoute}>
                                  <AddressCollectionEmail />
                                </Route>
                                <Route path={addressCollectionSMSRoute}>
                                  <AddressCollectionSMS />
                                </Route>
                                <Route path={sendUpdatesEmailRoute}>
                                  <SendUpdatesEmail />
                                </Route>
                                <Route path={shareDayOfDetailsEmailRoute}>
                                  <ShareDayOfDetailsEmail />
                                </Route>
                                <Route path={shareDayOfDetailsSMSRoute}>
                                  <ShareDayOfDetailsSMS />
                                </Route>
                                <Route path={sendUpdatesSMSRoute}>
                                  <SendUpdatesSMS />
                                </Route>
                                <Route path={livestreamEmailRoute}>
                                  <LivestreamEmail />
                                </Route>
                                <Route path={rsvpDeadlineReminderEmailRoute}>
                                  <RsvpDeadlineReminderEmail />
                                </Route>
                                <Route path={rsvpDeadlineReminderSmsRoute}>
                                  <RsvpDeadlineReminderSms />
                                </Route>
                                <Route path={shareLocalHotelEmailRoute}>
                                  <ShareLocalHotelEmail />
                                </Route>
                                <Route path={shareLocalHotelSMSRoute}>
                                  <ShareLocalHotelSMS />
                                </Route>
                                {/* The Sequences route need to be before the messaging hub route */}
                                <Route path={messagingHubRoute}>
                                  <MessagingHub />
                                </Route>
                                {/* this route is required for showing nested dashboard routes, i.e. right rails */}
                                <Route path="/gs/dashboard">
                                  <Main />
                                </Route>
                                {/* exact route is required for showing dashboard home route and exiting/closing of rails */}
                                <Route exact path={['/', '/gs/dashboard']}>
                                  <Main />
                                </Route>
                              </Switch>
                            </SafeCropContext.Provider>
                            <Switch>
                              <Route path="/our-guest-list">
                                <GuestListManagerApp />
                              </Route>
                            </Switch>
                          </React.Suspense>
                        </Router>
                      </Layout>
                    </Route>
                  </Switch>
                </Router>
              </AiTextGenerationProvider>
            </AiTextExperimentProvider>
          </WebsiteLayoutProvider>
        </TrackingProvider>
      </VariantProvider>
    </FeatureToggleProvider>
  );
};
export default App;
